import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyField,
  usePropertyAddressUrl,
  useTokens,
  useEditMode,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  EqualHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
  repliWhiteLogoUrl,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { useMemo } from 'react';
import { relativeLuminance } from '../utils/luminance';
import { handleScheduleTourClick, scheduleTourHref } from '../utils/openScheduleTour';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';

// ============================================================================
// Helpers
// ============================================================================

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

function formatAddress(raw: string | null | undefined): string {
  if (!raw) return '';
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  // Strip a trailing country segment (USA, U.S.A., US, United States, etc.).
  const filtered = parts.filter(
    (p) => !/^(u\.?s\.?a\.?|u\.?s\.?|united states( of america)?)$/i.test(p),
  );
  if (filtered.length === 0) return raw;
  // Move the last two comma-segments (city + "ST ZIP") onto their own line
  // so the street sits on line 1 and City, ST ZIP on line 2.
  if (filtered.length >= 3) {
    const street = filtered.slice(0, -2).join(', ');
    const cityStateZip = filtered.slice(-2).join(', ');
    return `${street}\n${cityStateZip}`;
  }
  return filtered.join(', ');
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined' && normalized !== '0' && normalized !== 'closed';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  label: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const entriesByDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !entriesByDay.has(key)) {
      entriesByDay.set(key, entry);
    }
  }

  const groups: GroupedHours[] = [];

  for (const key of DAY_ORDER) {
    const entry = entriesByDay.get(key);
    const dayLabel = DAY_LABELS[key];
    const isClosed = !entry || !hasValue(entry.open_time) || !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const ctaBackgroundDef: SlotGroupDefinition = {
  label: 'CTA Background Image',
  description: 'Wide background image shown behind the pre-footer call-to-action band.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
      suggestedFields: ['property_featured_image'],
    },
  },
};

const ctaContactLinkDef: SlotGroupDefinition = {
  label: 'CTA · Contact Us Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'CONTACT US' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const ctaTourLinkDef: SlotGroupDefinition = {
  label: 'CTA · Schedule a Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'SCHEDULE A TOUR' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  fields: {
    text: { label: 'Phone Text', type: 'text', defaultField: 'property_call_tracking_phone' },
    url: { label: 'URL', type: 'url', defaultField: 'property_call_tracking_phone' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const serviceManagerPhoneDef: SlotGroupDefinition = {
  label: 'Service Manager Phone',
  description: 'Emergency service manager phone displayed inside the emergency contact block. Falls back to the property call-tracking phone if left blank.',
  fields: {
    text: { label: 'Phone Text', type: 'text', defaultField: 'property_call_tracking_phone' },
    url: { label: 'URL', type: 'url', defaultField: 'property_call_tracking_phone' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const needHelpLinkDef: SlotGroupDefinition = {
  label: 'Need Help Link',
  description: '"Need Help? Start Here!" call-to-action for the emergency contact block. Points at a resident help / service-request landing page.',
  fields: {
    label: { label: 'Link Text', type: 'text', defaultValue: 'Need Help? Start Here!' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://nj211.org/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const emergencyBadgeDef: SlotGroupDefinition = {
  label: 'Emergency Badge Image',
  description: 'Optional emergency-services badge image (e.g. NJ 2-1-1) shown at the bottom of the emergency contact block.',
  fields: {
    image: {
      label: 'Badge Image',
      type: 'image',
      defaultValue: 'https://lirp.cdn-website.com/62dcea55/dms3rep/multi/opt/NJ211-Logo-web-352px-1920w.png',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'New Jersey 2-1-1 — Help Starts Here' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://nj211.org/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_logo', suggestedFields: ['property_company_logo', 'property_company_mono_black_logo', 'property_company_mono_white_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility Statement' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const tiktokLinkDef: SlotGroupDefinition = {
  label: 'TikTok',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_tiktok_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'TikTok' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  useSiteCtaStyles();
  const propertyName = useContentToken('footer.property_name');
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string | null | undefined;
  const propertyPhone = usePropertyField('property_phone') as string | null | undefined;
  const propertyAddressState = usePropertyField('property_address_state') as string | null | undefined;
  const propertyAddressUrl = usePropertyAddressUrl();
  // NJ-only emergency contact block (2-1-1). Hidden when state is missing
  // (company-theme preview) or anything other than New Jersey.
  const showEmergencyContact = (propertyAddressState || '').trim().toUpperCase() === 'NJ';

  // Aion footer sits on the theme's primary color. Use the standard
  // luminance check so a light-primary theme swaps to a black managed-by
  // logo instead of a white one that would disappear on the light band.
  const tokens = useTokens();
  const useDarkLogo = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return false;
    return relativeLuminance(primary) > 0.55;
  }, [tokens]);

  const effectiveLogoDef = useMemo<SlotGroupDefinition>(() => ({
    ...logoSlotDef,
    fields: {
      ...logoSlotDef.fields,
      image: {
        ...logoSlotDef.fields.image,
        defaultField: useDarkLogo ? 'property_mono_black_logo' : 'property_mono_white_logo',
      },
    },
  }), [useDarkLogo]);

  const companyMonoBlackLogo = usePropertyField('property_company_mono_black_logo') as string | null | undefined;
  const companyMonoWhiteLogo = usePropertyField('property_company_mono_white_logo') as string | null | undefined;
  const companyColorLogo = usePropertyField('property_company_logo') as string | null | undefined;

  const effectiveManagedByLogoDef = useMemo<SlotGroupDefinition>(() => {
    const preferredField = useDarkLogo
      ? 'property_company_mono_black_logo'
      : 'property_company_mono_white_logo';
    const otherMonoField = useDarkLogo
      ? 'property_company_mono_white_logo'
      : 'property_company_mono_black_logo';
    const preferredValue = useDarkLogo ? companyMonoBlackLogo : companyMonoWhiteLogo;
    const otherMonoValue = useDarkLogo ? companyMonoWhiteLogo : companyMonoBlackLogo;

    let resolvedField: string;
    if (preferredValue) resolvedField = preferredField;
    else if (otherMonoValue) resolvedField = otherMonoField;
    else if (companyColorLogo) resolvedField = 'property_company_logo';
    else resolvedField = preferredField;

    return {
      ...managedByLogoDef,
      fields: {
        ...managedByLogoDef.fields,
        image: {
          ...managedByLogoDef.fields.image,
          defaultField: resolvedField,
        },
      },
    };
  }, [useDarkLogo, companyMonoBlackLogo, companyMonoWhiteLogo, companyColorLogo]);

  // Slot groups
  const ctaBackground = useSlotGroup('footer.cta_background', ctaBackgroundDef);
  const ctaContactLink = useSlotGroup('footer.cta_contact_link', ctaContactLinkDef);
  const ctaTourLink = useSlotGroup('footer.cta_tour_link', ctaTourLinkDef);
  const logo = useSlotGroup('footer.logo', effectiveLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  const phoneLink = useSlotGroup('footer.phone_link', phoneLinkDef);
  const serviceManagerPhone = useSlotGroup('footer.service_manager_phone', serviceManagerPhoneDef);
  const needHelpLink = useSlotGroup('footer.need_help_link', needHelpLinkDef);
  const emergencyBadge = useSlotGroup('footer.emergency_badge', emergencyBadgeDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', effectiveManagedByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const tiktokLink = useSlotGroup('footer.tiktok_link', tiktokLinkDef);
  const isEditMode = Boolean(useEditMode()?.isEditMode);

  // Office hours
  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // Derived URLs
  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const phoneHref = toTelUrl(phoneLink.url) || toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);
  const phoneDisplay = formatPhone(phoneLink.text) || formatPhone(callTrackingPhone) || formatPhone(propertyPhone) || '(555) 555-5555';

  const serviceManagerHref = toTelUrl(serviceManagerPhone.url) || toTelUrl(serviceManagerPhone.text) || phoneHref;
  const serviceManagerDisplay = formatPhone(serviceManagerPhone.text) || phoneDisplay;

  return (
    <footer className="footer-root">

      {/* ============================================================ */}
      {/* Pre-footer CTA band                                          */}
      {/* ============================================================ */}
      <section className="footer-cta" aria-label="Contact call-to-action">
        <EditableSlotGroup groupPath="footer.cta_background" definition={ctaBackgroundDef}>
          <div className="footer-cta-media" aria-hidden="true">
            {ctaBackground.image ? (
              <VibesitesImage
                url={ctaBackground.image}
                alt=""
                sizes="100vw"
                className="footer-cta-image"
              />
            ) : null}
          </div>
        </EditableSlotGroup>
        <div className="footer-cta-tint" aria-hidden="true" />

        <div className="footer-cta-inner max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <RichTextContent
            path="footer.cta_headline"
            defaultValue='<p>Live your best life at <span data-type="field-token" data-field="property_name"></span>.</p>'
            as="h2"
            className="footer-cta-headline"
          />

          <div className="footer-cta-actions">
            <EditableSlotGroup groupPath="footer.cta_contact_link" definition={ctaContactLinkDef}>
              <a
                href={ctaContactLink.url || '/contact-us'}
                target={ctaContactLink._target || '_self'}
                rel={ctaContactLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                className="footer-cta-button"
              >
                {ctaContactLink.label || 'CONTACT US'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.cta_tour_link" definition={ctaTourLinkDef}>
              <a
                href={scheduleTourHref(ctaTourLink.url)}
                target={ctaTourLink._target || '_self'}
                rel={ctaTourLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                className="footer-cta-button"
                data-vibesite-event="schedule_tour_clicked"
                onClickCapture={(e) =>
                  handleScheduleTourClick(e, ctaTourLink.url || '/schedule-a-tour')
                }
              >
                {ctaTourLink.label || 'SCHEDULE A TOUR'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4 md:pt-10 md:pb-5">

        {/* ============================================================ */}
        {/* Main grid: logo + info columns                              */}
        {/* ============================================================ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.1fr_1fr_1.3fr_1.1fr] gap-y-6 gap-x-8">

          {/* Logo + socials */}
          <div className="flex flex-col items-center sm:items-start gap-4">
            <EditableSlotGroup groupPath="footer.logo" definition={effectiveLogoDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    sizes="220px"
                    className="h-20 w-auto max-w-[220px] object-contain object-left"
                  />
                ) : (
                  <span className="footer-heading text-2xl">
                    {propertyName || 'Community'}
                  </span>
                )}
              </a>
            </EditableSlotGroup>

            <div className="flex items-center gap-4">
              <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                {facebookLink.url ? (
                  <a
                    href={facebookLink.url}
                    target={facebookLink._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={facebookLink.label || 'Facebook'}
                    className="footer-social"
                  >
                    <FontAwesomeIcon icon={faFacebookF} className="text-xl" />
                  </a>
                ) : (
                  <span className="footer-social opacity-40" aria-hidden="true">
                    <FontAwesomeIcon icon={faFacebookF} className="text-xl" />
                  </span>
                )}
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                {instagramLink.url ? (
                  <a
                    href={instagramLink.url}
                    target={instagramLink._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={instagramLink.label || 'Instagram'}
                    className="footer-social"
                  >
                    <FontAwesomeIcon icon={faInstagram} className="text-xl" />
                  </a>
                ) : (
                  <span className="footer-social opacity-40" aria-hidden="true">
                    <FontAwesomeIcon icon={faInstagram} className="text-xl" />
                  </span>
                )}
              </EditableSlotGroup>
              {tiktokLink.url || isEditMode ? (
                <EditableSlotGroup groupPath="footer.tiktok_link" definition={tiktokLinkDef}>
                  {tiktokLink.url ? (
                    <a
                      href={tiktokLink.url}
                      target={tiktokLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={tiktokLink.label || 'TikTok'}
                      className="footer-social"
                    >
                      <FontAwesomeIcon icon={faTiktok} className="text-xl" />
                    </a>
                  ) : (
                    <span className="footer-social opacity-40" aria-hidden="true">
                      <FontAwesomeIcon icon={faTiktok} className="text-xl" />
                    </span>
                  )}
                </EditableSlotGroup>
              ) : null}
            </div>
          </div>

          {/* CONTACT US */}
          <div className="footer-col">
            <RichTextContent
              path="footer.contact_label"
              defaultValue="<p>CONTACT US</p>"
              as="h3"
              className="footer-col-heading"
            />
            <EditableSlotGroup groupPath="footer.phone_link" definition={phoneLinkDef}>
              <a
                href={phoneHref || '#'}
                className="footer-link dniphone dniphonehref"
              >
                {phoneDisplay}
              </a>
            </EditableSlotGroup>
          </div>

          {/* VISIT US */}
          <div className="footer-col">
            <RichTextContent
              path="footer.visit_label"
              defaultValue="<p>VISIT US</p>"
              as="h3"
              className="footer-col-heading"
            />
            <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
              <a
                href={addressHref}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="footer-link footer-link-address"
              >
                {formatAddress(addressLink.text) || '123 Main Street\nCity, ST 00000'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* HOURS */}
          <div className="footer-col">
            <RichTextContent
              path="footer.hours_label"
              defaultValue="<p>HOURS</p>"
              as="h3"
              className="footer-col-heading"
            />
            {groupedHours.length > 0 ? (
              <dl className="footer-hours">
                {groupedHours.map((group, index) => (
                  <div key={index} className="footer-hours-row">
                    <dt>{group.label}</dt>
                    <dd>{group.hours}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="footer-link opacity-60">Office hours will be displayed here.</p>
            )}
          </div>

        </div>

        {/* ============================================================ */}
        {/* Emergency contact information block (NJ properties only)     */}
        {/* ============================================================ */}
        {showEmergencyContact ? (
          <div className="footer-emergency mt-8 md:mt-10">
            <RichTextContent
              path="footer.emergency_heading"
              defaultValue='<p>Emergency Contact Information | Información de Contacto en caso de Emergencia</p>'
              as="p"
              className="footer-emergency-line"
            />
            <p className="footer-emergency-line">
              <EditableToken path="footer.property_name">
                {propertyName || 'Community'}
              </EditableToken>
              {' Service Manager: '}
              <EditableSlotGroup groupPath="footer.service_manager_phone" definition={serviceManagerPhoneDef}>
                <a
                  href={serviceManagerHref || '#'}
                  className="footer-emergency-phone dniphone dniphonehref"
                >
                  {serviceManagerDisplay}
                </a>
              </EditableSlotGroup>
            </p>

            <EditableSlotGroup groupPath="footer.need_help_link" definition={needHelpLinkDef}>
              <a
                href={needHelpLink.url || 'https://nj211.org/'}
                target={needHelpLink._target || '_blank'}
                rel="noopener noreferrer"
                className="footer-need-help"
              >
                {needHelpLink.label || 'Need Help? Start Here!'}
              </a>
            </EditableSlotGroup>

            {emergencyBadge.image ? (
              <EditableSlotGroup groupPath="footer.emergency_badge" definition={emergencyBadgeDef}>
                {emergencyBadge.url ? (
                  <a
                    href={emergencyBadge.url}
                    target={emergencyBadge._target || '_blank'}
                    rel="noopener noreferrer"
                    className="footer-emergency-badge-link"
                  >
                    <VibesitesImage
                      url={emergencyBadge.image}
                      alt={emergencyBadge.alt || 'Emergency Badge'}
                      sizes="100px"
                      className="h-14 w-auto object-contain"
                    />
                  </a>
                ) : (
                  <VibesitesImage
                    url={emergencyBadge.image}
                    alt={emergencyBadge.alt || 'Emergency Badge'}
                    sizes="100px"
                    className="h-14 w-auto object-contain footer-emergency-badge-link"
                  />
                )}
              </EditableSlotGroup>
            ) : null}
          </div>
        ) : null}

      </div>

      {/* ============================================================ */}
      {/* Attribution band                                             */}
      {/* ============================================================ */}
      <div className="footer-attr-band">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 pb-5 md:pt-4 md:pb-6">
          <div className="footer-attr-divider" aria-hidden="true" />

          <div className="footer-attr-grid mt-5">

            {/* Managed by */}
            <div className="footer-attr-left">
              {managedByLogo.image ? (
                <div className="footer-attr-group">
                  <span className="footer-attr-label">Managed by</span>
                  <EditableSlotGroup groupPath="footer.managed_by_logo" definition={effectiveManagedByLogoDef}>
                    <a
                      href={managedByLogo.url || '#'}
                      target={managedByLogo._target || '_blank'}
                      rel="noopener noreferrer"
                      className="opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <VibesitesImage
                        url={managedByLogo.image}
                        alt={managedByLogo.alt || 'Property Management Company'}
                        sizes="220px"
                        className="h-12 w-auto max-w-[220px] object-contain"
                      />
                    </a>
                  </EditableSlotGroup>
                </div>
              ) : null}
            </div>

            {/* Compliance + copyright + legal (center) */}
            <div className="footer-attr-center">
              <div className="footer-compliance" aria-label="Fair housing and accessibility">
                <EqualHousingIcon size={26} className="footer-compliance-icon" aria-label="Equal Housing Opportunity" />
                <PetsAllowedIcon size={22} className="footer-compliance-icon" aria-label="Pet Friendly" />
                <WheelchairIcon size={22} className="footer-compliance-icon" aria-label="Accessibility" />
              </div>

              <p className="footer-copyright">
                &copy; {new Date().getFullYear()} All Rights Reserved.{' '}
                <EditableToken
                  path="footer.property_name"
                  defaultField="property_name"
                  defaultValue="Community"
                >
                  {propertyName || 'Community'}
                </EditableToken>
              </p>

              <nav className="footer-legal" aria-label="Footer legal links">
                <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                  <a
                    href={privacyPolicyLink.url || '/privacy-policy'}
                    target={privacyPolicyLink._target || '_self'}
                    className="footer-legal-link"
                  >
                    {privacyPolicyLink.text || 'Privacy Policy'}
                  </a>
                </EditableSlotGroup>
                <span className="footer-legal-sep" aria-hidden="true">·</span>
                <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                  <a
                    href={accessibilityLink.url || '/accessibility'}
                    target={accessibilityLink._target || '_self'}
                    className="footer-legal-link"
                  >
                    {accessibilityLink.text || 'Accessibility Statement'}
                  </a>
                </EditableSlotGroup>
                <span className="footer-legal-sep" aria-hidden="true">·</span>
                <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                  <a
                    href={sitemapLink.url || '/sitemap'}
                    target={sitemapLink._target || '_self'}
                    className="footer-legal-link"
                  >
                    {sitemapLink.text || 'Sitemap'}
                  </a>
                </EditableSlotGroup>
              </nav>
            </div>

            {/* Designed by (right) */}
            <div className="footer-attr-right">
              <div className="footer-attr-group">
                <span className="footer-attr-label">Designed by</span>
                <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
                  <a
                    href={designByLogo.url || 'https://www.repli360.com/'}
                    target={designByLogo._target || '_blank'}
                    rel="noopener noreferrer"
                    className="opacity-90 hover:opacity-100 transition-opacity"
                  >
                    <span className="footer-repli-mark">
                      <img
                        src={repliWhiteLogoUrl}
                        alt={designByLogo.alt || 'Repli'}
                        className="h-10 w-auto object-contain block"
                        style={useDarkLogo ? { filter: 'invert(1)' } : undefined}
                      />
                      <span
                        className="footer-repli-dot"
                        aria-hidden="true"
                        style={{ background: useDarkLogo ? '#000' : '#fff' }}
                      />
                    </span>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* Scoped Styles                                                   */}
      {/* ================================================================ */}
      <style>{`
        .footer-root {
          --footer-ink: var(--vibe-color-text-on-primary, #ffffff);
          background-color: var(--vibe-color-primary, #12244a);
          color: var(--footer-ink);
        }

        /* Pre-footer CTA band -------------------------------------------- */
        .footer-cta {
          position: relative;
          overflow: hidden;
          isolation: isolate;
          background-color: var(--vibe-color-primary, #12244a);
        }
        .footer-cta-media {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .footer-cta-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }
        .footer-cta-tint {
          position: absolute;
          inset: 0;
          z-index: 1;
          background: color-mix(in srgb, var(--vibe-color-primary, #12244a) 78%, transparent);
        }
        .footer-cta-inner {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          gap: 1.25rem;
          padding-top: 2rem;
          padding-bottom: 2rem;
          text-align: center;
        }
        .footer-cta-headline {
          font-family: var(--vibe-font-heading, sans-serif);
          font-size: clamp(1.75rem, 3.6vw, 2.75rem);
          font-weight: 700;
          line-height: 1.15;
          color: var(--footer-ink);
          margin: 0;
          max-width: 22ch;
          text-wrap: balance;
        }
        .footer-cta-headline p,
        .footer-cta-headline span {
          font: inherit;
          color: inherit;
          margin: 0;
        }
        /* Equal-width CTAs: column tracks size to the longer label. */
        .footer-cta-actions {
          display: inline-grid;
          grid-template-columns: 1fr;
          gap: 0.75rem 1rem;
          align-items: stretch;
          justify-items: stretch;
        }
        .footer-cta-actions > * {
          display: flex;
          min-width: 0;
        }
        /* Keep footer CTA sizing; color treatment matches Header .sh-book-btn. */
        .footer-cta-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          box-sizing: border-box;
          min-width: 13.5rem;
          padding: 1.1rem 2.5rem;
          border-radius: 999px;
          background: #fff;
          border: 1px solid #fff;
          color: var(--vibe-color-primary, #12244a);
          font-family: var(--vibe-font-heading, sans-serif);
          font-size: 1rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          line-height: 1.25;
          white-space: nowrap;
          text-decoration: none;
          transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
        }
        .footer-cta-button:hover,
        .footer-cta-button:focus-visible {
          background: var(--vibe-color-surface, #fff);
          border-color: var(--vibe-color-surface, #fff);
          color: var(--site-cta-ink-on-surface, #fff);
          transform: translateY(-1px);
        }
        .footer-cta-button:focus-visible {
          outline: 2px solid #ffffff;
          outline-offset: 3px;
        }
        /* Side-by-side CTAs need ~27rem + headline; wait until lg so tablet
           keeps stacked buttons and avoids clipping. */
        @media (min-width: 1024px) {
          .footer-cta-inner {
            flex-direction: row;
            text-align: left;
            padding-top: 2.5rem;
            padding-bottom: 2.5rem;
            gap: 2rem;
          }
          .footer-cta-headline {
            max-width: 28ch;
          }
          .footer-cta-actions {
            grid-template-columns: 1fr 1fr;
            flex-shrink: 0;
          }
        }

        .footer-heading {
          font-family: var(--vibe-font-heading, sans-serif);
          font-weight: 700;
          color: var(--footer-ink);
          line-height: 1.1;
        }
        .footer-col {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .footer-col-heading {
          font-family: var(--vibe-font-heading, sans-serif);
          font-size: 1.05rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--footer-ink);
          margin-bottom: 0.5rem;
        }
        .footer-link {
          font-family: var(--vibe-font-body, sans-serif);
          font-size: 1rem;
          line-height: 1.5;
          color: var(--footer-ink);
          text-decoration: none;
          transition: opacity 0.2s ease;
        }
        .footer-link:hover {
          opacity: 0.7;
        }
        .footer-link-address {
          white-space: pre-line;
        }
        .footer-social {
          color: var(--footer-ink);
          transition: opacity 0.2s ease;
        }
        .footer-social:hover {
          opacity: 0.7;
        }

        .footer-hours {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }
        .footer-hours-row {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 1.25rem;
          align-items: baseline;
          font-family: var(--vibe-font-body, sans-serif);
          font-size: 1rem;
          color: var(--footer-ink);
        }
        .footer-hours-row dt {
          font-weight: 400;
          white-space: nowrap;
        }
        .footer-hours-row dd {
          white-space: nowrap;
          text-align: right;
        }

        /* Emergency block ----------------------------------------------- */
        .footer-emergency {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.35rem;
        }
        .footer-emergency-line {
          font-family: var(--vibe-font-body, sans-serif);
          font-size: 1rem;
          line-height: 1.5;
          color: var(--footer-ink);
          margin: 0;
        }
        .footer-emergency-phone {
          color: var(--footer-ink);
          text-decoration: none;
          transition: opacity 0.2s ease;
        }
        .footer-emergency-phone:hover {
          opacity: 0.7;
        }
        .footer-need-help {
          font-family: var(--vibe-font-heading, sans-serif);
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--footer-ink);
          text-decoration: none;
          margin-top: 0.85rem;
          transition: opacity 0.2s ease;
        }
        .footer-need-help:hover {
          opacity: 0.7;
        }
        .footer-emergency-badge-link {
          margin-top: 0.75rem;
          display: inline-block;
          transition: opacity 0.2s ease;
        }
        .footer-emergency-badge-link:hover {
          opacity: 0.85;
        }

        /* Attribution band ---------------------------------------------- */
        .footer-attr-band {
          background-color: var(--vibe-color-primary, #12244a);
          color: var(--footer-ink);
        }
        .footer-attr-divider {
          border-top: 2px solid color-mix(in srgb, var(--footer-ink) 35%, transparent);
        }
        .footer-attr-grid {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          gap: 1.5rem 2.5rem;
        }
        .footer-attr-left {
          justify-self: start;
          display: flex;
          align-items: center;
        }
        .footer-attr-center {
          justify-self: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          text-align: center;
        }
        .footer-attr-right {
          justify-self: end;
          display: flex;
          align-items: center;
        }
        .footer-attr-group {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.4rem;
        }
        .footer-attr-right .footer-attr-group {
          align-items: flex-end;
        }
        .footer-attr-label {
          font-family: var(--vibe-font-body, sans-serif);
          font-size: 0.85rem;
          font-weight: 400;
          letter-spacing: 0.02em;
          color: var(--footer-ink);
        }
        .footer-compliance {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
          color: var(--footer-ink);
        }
        .footer-compliance-icon {
          color: var(--footer-ink);
        }
        .footer-copyright {
          font-family: var(--vibe-font-body, sans-serif);
          font-size: 0.85rem;
          color: color-mix(in srgb, var(--footer-ink) 90%, transparent);
          margin: 0;
          white-space: nowrap;
        }
        .footer-legal {
          display: flex;
          flex-wrap: nowrap;
          align-items: center;
          justify-content: center;
          gap: 0.35rem 0.65rem;
        }
        .footer-legal-link {
          font-family: var(--vibe-font-body, sans-serif);
          font-size: 0.75rem;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--footer-ink);
          text-decoration: none;
          transition: opacity 0.2s ease;
          white-space: nowrap;
        }
        .footer-legal-link:hover {
          opacity: 0.7;
          text-decoration: underline;
        }
        .footer-legal-sep {
          color: color-mix(in srgb, var(--footer-ink) 55%, transparent);
        }

        /* Repli mark ---------------------------------------------------- */
        .footer-repli-mark {
          position: relative;
          display: inline-block;
          line-height: 0;
        }
        .footer-repli-dot {
          position: absolute;
          top: 17.84%;
          left: 96.14%;
          width: 7.09%;
          aspect-ratio: 1 / 1;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
        }

        /* Mobile stacking ----------------------------------------------- */
        @media (max-width: 767.98px) {
          .footer-col {
            align-items: center;
            text-align: center;
          }
          .footer-hours-row {
            grid-template-columns: auto auto;
            gap: 0.75rem;
            justify-content: center;
          }
          .footer-emergency {
            align-items: center;
            text-align: center;
          }
          .footer-attr-grid {
            grid-template-columns: 1fr;
            gap: 1.25rem;
          }
          .footer-attr-left,
          .footer-attr-center,
          .footer-attr-right {
            justify-self: center;
          }
          .footer-attr-group {
            align-items: center;
            text-align: center;
          }
          /* Keep © line on one row; scale down so typical names fit. */
          .footer-copyright {
            font-size: clamp(0.68rem, 2.85vw, 0.85rem);
          }
          .footer-legal {
            gap: 0.25rem 0.4rem;
          }
          .footer-legal-link {
            font-size: 0.65rem;
            letter-spacing: 0.02em;
          }
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.property_name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Community',
    },
    'footer.cta_headline': {
      label: 'Pre-Footer CTA Headline',
      type: 'richtext',
      defaultValue:
        '<p>Live your best life at <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
    'footer.contact_label': {
      label: 'Contact Us Column Heading',
      type: 'richtext',
      defaultValue: '<p>CONTACT US</p>',
    },
    'footer.visit_label': {
      label: 'Visit Us Column Heading',
      type: 'richtext',
      defaultValue: '<p>VISIT US</p>',
    },
    'footer.hours_label': {
      label: 'Hours Column Heading',
      type: 'richtext',
      defaultValue: '<p>HOURS</p>',
    },
    'footer.emergency_heading': {
      label: 'Emergency Contact Heading',
      type: 'richtext',
      defaultValue:
        '<p>Emergency Contact Information | Información de Contacto en caso de Emergencia</p>',
    },
  },
  slotGroups: {
    'footer.cta_background': ctaBackgroundDef,
    'footer.cta_contact_link': ctaContactLinkDef,
    'footer.cta_tour_link': ctaTourLinkDef,
    'footer.logo': logoSlotDef,
    'footer.address': addressLinkDef,
    'footer.phone_link': phoneLinkDef,
    'footer.service_manager_phone': serviceManagerPhoneDef,
    'footer.need_help_link': needHelpLinkDef,
    'footer.emergency_badge': emergencyBadgeDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.tiktok_link': tiktokLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Footer;
